import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Bell, 
  Building2, 
  Clock, 
  Target, 
  Zap,
  CheckCircle,
  TrendingUp,
  FileText,
  BarChart3,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function JobFinderHub() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [location, setLocation] = useState("");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "One-Time Setup",
      description: "A quick 15-minute sign-up process customizes your alerts and preferences. After that, you never have to log into individual job boards again."
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Real-Time Instant Alerts",
      description: "The moment a new job that fits your criteria appears online — whether it's on your favorite company's careers page or a major job board — you get notified instantly."
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Favorite Companies Tracking",
      description: "Add the companies you want to work for and TailorWiz will alert you the moment any new role opens up, giving you the chance to be among the very first applicants."
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Seamless Resume Integration",
      description: "Pair every new posting with a TailorWiz-tailored resume package so you can apply in minutes with materials already optimized for that exact role."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Unified Job Tracking",
      description: "Instead of managing spreadsheets or sticky notes, track every opportunity in one place. See which jobs you've applied for, which you're watching, and which you've marked as top priorities."
    }
  ];

  const benefits = [
    {
      icon: <Target className="w-5 h-5" />,
      text: "Auto-categorization of jobs by industry, company, or application status"
    },
    {
      icon: <Bell className="w-5 h-5" />,
      text: "Smart reminders so you never miss a follow-up or deadline"
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      text: "Saved criteria so your alerts match exactly what you want without extra filtering"
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      text: "All-in-one visibility so you always know the status of your search at a glance"
    }
  ];

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/')}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Job Finder & Tracking Hub</h1>
                  <p className="text-sm text-muted-foreground">600+ job boards in one powerful platform</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-5xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight">
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Job Finder{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      & Tracking Hub
                    </span>
                  </h1>
                  
                  <div className="max-w-4xl mx-auto px-4 mt-4">
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      The Advanced Job Finder & Tracking Hub brings the top 600+ online job boards and career resources into one seamless portal. 
                      No more jumping between Indeed, LinkedIn, Google Jobs, ZipRecruiter, Monster, CareerBuilder, Ladders, and countless other sites.
                    </p>
                  </div>
                </div>
              </div>
              {/* Quick Setup Form */}
              <Card className="max-w-2xl mx-auto bg-card/95 shadow-2xl border-border/20 mb-12">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-6 text-center text-primary">Start Your 15-Minute Setup</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="jobTitle">Job Title</Label>
                      <Input
                        id="jobTitle"
                        placeholder="e.g., Software Engineer"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        placeholder="e.g., San Francisco, CA"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    size="lg" 
                    className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 text-lg rounded-xl"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Start Using the Job Finder & Tracking Hub Today
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Features Section */}
            <section className="py-20">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
                  Here's what makes it different:
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                    {features.map((feature, index) => (
                      <Card key={index} className="border-2 hover:border-primary/50 transition-colors duration-300">
                        <CardContent className="p-6">
                          <div className="flex items-center mb-4">
                            <div className="p-3 rounded-full bg-primary/10 text-primary mr-4">
                              {feature.icon}
                            </div>
                            <h3 className="text-xl font-semibold">{feature.title}</h3>
                          </div>
                          <p className="text-muted-foreground leading-relaxed">
                            {feature.description}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            </section>

            {/* First in Line Section */}
            <section className="py-20 bg-gradient-to-r from-primary/5 to-secondary/5">
              <div className="max-w-4xl mx-auto text-center">
                  <Badge variant="outline" className="mb-4 text-lg py-2 px-4">
                    <Zap className="w-4 h-4 mr-2" />
                    Competitive Advantage
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                    Always Be First in Line
                  </h2>
                  <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                    Being first matters. Studies show that candidates who apply within the first 24–48 hours of a job posting are 
                    <span className="font-semibold text-primary"> up to 8 times more likely</span> to get an interview than those who apply later. 
                    With TailorWiz, you don't just see jobs faster — you act faster with a fully tailored resume ready to go.
                  </p>
                  <div className="bg-card/80 backdrop-blur-sm rounded-xl p-8 border">
                    <p className="text-xl font-medium">
                      Instead of competing as the 250th applicant, you stand out as one of the first.
                    </p>
                  </div>
              </div>
            </section>

            {/* Smart Automation Section */}
            <section className="py-20">
              <div className="max-w-6xl mx-auto">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                      Smart Automation That Works For You
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                      The Tracking Hub was designed to make the job search easier, not harder. 
                      With state-of-the-art automation, you'll enjoy:
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-4 p-6 rounded-lg bg-card/50 border">
                        <div className="flex-shrink-0 p-2 rounded-full bg-primary/10 text-primary">
                          {benefit.icon}
                        </div>
                        <p className="text-lg">{benefit.text}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-12 text-center">
                    <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                      No more scattered tabs, duplicate alerts, or missed chances. Everything is in one hub, 
                      always up to date, always working in your favor.
                    </p>
                  </div>
              </div>
            </section>

            {/* Future of Job Searching */}
            <section className="py-20 bg-gradient-to-br from-secondary/5 via-background to-primary/5">
              <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                    The Future of Job Searching
                  </h2>
                  <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                    This isn't just a job board. It's a system that ensures you never miss an opportunity again and 
                    always apply with the best possible version of your resume. Combined with TailorWiz's tailoring 
                    and analysis tools, the Advanced Job Finder & Tracking Hub turns job searching into a fast, 
                    organized, and highly effective process.
                  </p>
                  
                  <Button size="lg" className="text-lg px-8 py-6">
                    <Search className="w-5 h-5 mr-2" />
                    Start Using the Job Finder & Tracking Hub Today
                  </Button>
              </div>
            </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}